import { useLayoutEffect, useMemo, useRef, useState } from "react"
import { ThemeProvider } from "@mui/material/styles"
import { DrawerButton } from "./DrawerButton"
import Divider from "@mui/material/Divider"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import { Result } from "./Result"
import { Select } from "./Select"
import { Card } from "./Card"
import { CardGrid } from "./CardGrid"
import Acknowledgements from "./Acknowledgements"
import {
  DrawerContextProvider,
  useDrawerContext,
} from "./context/DrawerContext"
import {
  ChampionContextProvider,
  useChampionContext,
} from "./context/ChampionContext"
import {
  CardsContextProvider,
  useCardsContext,
  META_TIERS,
} from "./context/CardsContext"
import { SettingsProvider, useSettings } from "./context/SettingsContext"
import { AuthProvider, useAuthContext } from "./context/AuthContext"
import { UserMenu } from "./UserMenu"
import { LoginModal } from "./LoginModal"
import { MigrationDialog } from "./MigrationDialog"
import Dialog from "@mui/material/Dialog"
import { FeedbackFish } from "@feedback-fish/react"
import "./index.css"
import {
  AppSizeContextProvider,
  useAppSizeContext,
} from "./context/AppSizeContext"
import { ModeContextProvider, useModeContext } from "./context/ModeContext"
import {
  IgnoredContextProvider,
  useIgnoredContext,
} from "./context/IgnoredContext"
import { FormControl, InputLabel, MenuItem } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import ReplayIcon from "@mui/icons-material/Replay"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined"
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import SingleSelect from "@mui/material/Select"
import { DeckContextProvider } from "./context/DeckContext"
import { downloadCSV, uploadCSV } from "./utils/handle-csv"
import { event } from "./auth"
import { FloatingMemProvider } from "./context/FloatingMemory"
import { PrepCounterProvider } from "./context/PrepCounters"
import { MillEffectsProvider } from "./context/MillEffects"
import { GraveBanishProvider } from "./context/GraveBanish"
import { ExclusionFilters } from "./ExclusionFilters"
import { release } from "./release"
import { ManualChampPicker, Pile } from "./ManualChampPicker"
import { Tip, ToggleRow, Section } from "./ui"
import { getTheme, applyCssPalette } from "./theme"

export { Tip }

const metaTierTip = (
  <>
    Tiers rank cards by their tournament win rate over the past 60 days,
    sourced from fractalofin.site. Current cutoffs:
    <table style={{ margin: "6px 0 6px 8px", borderSpacing: 0 }}>
      <tbody>
        {META_TIERS.filter((t) => t.threshold).map((t) => (
          <tr key={t.value}>
            <td style={{ padding: "0 12px 0 0" }}>{t.name}</td>
            <td style={{ padding: 0, fontVariantNumeric: "tabular-nums" }}>
              {t.threshold}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    Cards with no recorded play in the window count as 0%. The win-rate
    pill on cards can be toggled in settings.
  </>
)

const menuSectionLabelStyle = {
  padding: "8px 14px 4px",
  fontSize: 11,
  color: "var(--muted)",
  textTransform: "uppercase",
  letterSpacing: 0.8,
  fontWeight: 600,
}

const menuItemStyle = {
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 10,
  paddingRight: 10,
}

// A play-cost / power / life row: numeric input + comparison operator.
const StatFilter = ({ label, value, setValue, op, setOp }) => (
  <div style={{ display: "flex", gap: 4 }}>
    <TextField
      disabled={!op}
      InputLabelProps={{ shrink: true }}
      size="small"
      type="number"
      label={label}
      value={value}
      onChange={(a) => {
        const val = parseInt(a.target.value)
        if (!Number.isInteger(val)) return
        if (val < 0) return
        setValue(val)
      }}
    />
    <FormControl size="small" style={{ flex: 1 }}>
      <SingleSelect
        value={op ?? "n/a"}
        onChange={(e) => {
          const next = e.target.value === "n/a" ? null : e.target.value
          setOp(next)
          if (next === null) {
            setValue(0)
          }
        }}
      >
        <MenuItem value={"n/a"}>any</MenuItem>
        <MenuItem value={"="}>{"= N"}</MenuItem>
        <MenuItem value={">"}>{"< N"}</MenuItem>
        <MenuItem value={"<"}>{"> N"}</MenuItem>
      </SingleSelect>
    </FormControl>
  </div>
)

const App = () => {
  const appSizeCtx = useAppSizeContext()
  const drawerCtx = useDrawerContext()
  const champCtx = useChampionContext()
  const cardsCtx = useCardsContext()
  const authCtx = useAuthContext()
  const modeCtx = useModeContext()
  const {
    state: settingsState,
    togglePrice,
    togglePrint,
    toggleOwned,
    toggleWinRate,
    toggleTheme,
  } = useSettings()
  const { ignoreEnabled, toggleIgnore, ignoredList, removeFromIgnored } =
    useIgnoredContext()
  const controlsRef = useRef()

  const [focusHover, setFocusHover] = useState(null)
  const focusCard = champCtx.focus?.[focusHover]

  // fit the hover preview into the space left below the lineup so the
  // card is never clipped by the bottom of the drawer
  const previewRef = useRef(null)
  const [previewHeight, setPreviewHeight] = useState(380)
  useLayoutEffect(() => {
    if (!focusCard) return
    const wrap = previewRef.current
    const scroller = controlsRef.current
    if (!wrap || !scroller) return
    const available =
      scroller.getBoundingClientRect().bottom -
      wrap.getBoundingClientRect().top -
      32
    setPreviewHeight(Math.max(240, Math.min(430, available)))
  }, [focusCard])

  const resultCount = <Result label={`${cardsCtx?.cards?.length} cards`} />

  const [fileName, setFileName] = useState(null)
  const fileInputRef = useRef(null)
  const [csvWarnOpen, setCsvWarnOpen] = useState(false)
  const triggerCsvPicker = () => {
    fileInputRef.current.click()
    event("upload-csv", "/collection")
  }
  const handleClick = () => {
    // the first CSV upload commits the account's collection (sets
    // collectionImported), which starts clearing stale device copies
    // on login — get explicit consent before that switch flips
    if (authCtx.cloudDoc?.collectionImported !== true) {
      setCsvWarnOpen(true)
      return
    }
    triggerCsvPicker()
  }

  const [trackedList, setTrackedList] = useState([])

  // settings menu state (markup only)
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null)
  const settingsOpen = Boolean(settingsAnchorEl)
  const handleSettingsClick = (event) =>
    setSettingsAnchorEl(event.currentTarget)
  const handleSettingsClose = () => setSettingsAnchorEl(null)

  const isDark = settingsState.themeMode === "dark"

  const isLoggedIn = !!authCtx.user

  const collectionSection = (
    <Section
      title="Collection"
      badge={isLoggedIn ? `$${cardsCtx.estInventoryValue}` : "$ --"}
      hint="manage"
      open={cardsCtx.inventoryEnabled}
      onToggle={() => {
        cardsCtx.setInventoryEnabled((b) => {
          const enabled = !b
          if (enabled) {
            event("enabled-inventory", "/collection")
          }
          // disable this filter if
          // collection manager is closed:
          if (!enabled) {
            cardsCtx.setOnlyIncompleteSets(false)
          }
          return enabled
        })
      }}
    >
      {cardsCtx.inventoryEnabled && (
        <>
          <div className="stat-row">
            <span className="stat-label">Missing for full playsets</span>
            <span className="stat-value">
              {isLoggedIn ? `$${cardsCtx.estMissingPlaysetValue}` : "$ --"}
            </span>
          </div>

          <div className="stat-row">
            <span className="stat-label">Every card in the game</span>
            <span className="stat-value">${cardsCtx.fullCollectionValue}</span>
            <Tip
              style={{ marginLeft: 0 }}
              msg="We value each card at its cheapest printing."
            />
          </div>

          <div className="section-divider" aria-hidden="true" />

          {isLoggedIn ? (
            <>
              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  style={{ padding: "3px 8px", fontSize: 12, flex: 1 }}
                  variant="outlined"
                  size="small"
                  disabled={!authCtx.cloudLoaded}
                  onClick={() => {
                    const escapeField = (field) => {
                      if (field.includes(",")) {
                        return field.replace(/,/g, "<comma>")
                      }
                      return field
                    }
                    const list = Object.entries(cardsCtx.inventory).map(
                      (ent) => [escapeField(ent[0]), ent[1]].join(","),
                    )
                    downloadCSV(list)
                    event("download-csv", "/collection")
                  }}
                >
                  Download CSV
                </Button>
                <Button
                  style={{ padding: "3px 8px", fontSize: 12, flex: 1 }}
                  variant="outlined"
                  size="small"
                  disabled={!authCtx.cloudLoaded}
                  onClick={handleClick}
                >
                  Upload CSV
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) =>
                    uploadCSV(e, cardsCtx.replaceInventory, setFileName)
                  }
                  style={{ display: "none" }}
                />
              </div>

              <p className="section-note">
                Synced to your account. Uploading a CSV
                <br />
                replaces your cloud collection.
              </p>

              {fileName && (
                <p style={{ fontSize: 12 }}>
                  <span style={{ color: "var(--muted)" }}>Uploaded:</span>{" "}
                  {fileName}
                </p>
              )}
            </>
          ) : (
            <p className="section-note">
              Collections can only be managed when
              <br />
              you're logged in.{" "}
              <button
                onClick={authCtx.openLogin}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "var(--accent)",
                  font: "inherit",
                }}
              >
                Log in
              </button>
            </p>
          )}

          {isLoggedIn && (
            <>
              <div className="section-divider" aria-hidden="true" />

              <ToggleRow
                label="Only show incomplete playsets"
                checked={cardsCtx.onlyIncompleteSets}
                onChange={() => {
                  cardsCtx.setOnlyIncompleteSets((b) => {
                    const enabled = !b
                    if (enabled) {
                      event("filtered-inventory", "/collection")
                    }
                    return enabled
                  })
                }}
              />

              <p className="section-note">
                A playset is 4 copies, or 1 for champions,
                <br />
                regalia, and pantheon-only cards.
              </p>
            </>
          )}
        </>
      )}
    </Section>
  )

  const lineupSection = (
    <Section title="Champion lineup" collapsible={false}>
      {(function () {
        const mapped = champCtx.focus?.reduce((acc, obj) => {
          ;(acc[obj.level] ||= []).push(obj)
          return acc
        }, {})
        if (!mapped) return null
        const maxList = Math.max(
          ...Object.values(mapped).map((list) => list.length),
        )
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "start",
              alignItems: "start",
              height: 100 + 14 * maxList,
              gap: 10,
              flex: "none",
            }}
          >
            {Object.values(mapped).map((list, idx) => {
              return (
                <Pile
                  type=""
                  key={idx}
                  cards={list}
                  height={100}
                  radius={4}
                  mult={14}
                  noBorder
                  onLeave={() => setFocusHover(null)}
                  onEnter={(card) => {
                    const idx = champCtx.focus.findIndex(
                      (c) => c.name === card.name,
                    )
                    setFocusHover(idx)
                  }}
                />
              )
            })}
          </div>
        )
      })()}
    </Section>
  )

  const synergiesSection = (
    <Section title="Synergies" collapsible={false}>
      <div className="group-label">Lineup bonuses</div>
      <ToggleRow
        label="Only Champion elements"
        checked={cardsCtx.onlyChampElements}
        onChange={() => cardsCtx.setOnlyChampElements((b) => !b)}
        tip="Based on the selected champion lineup"
      />
      <ToggleRow
        label="Floating Memory"
        checked={cardsCtx.withFloatingMem}
        onChange={() => cardsCtx.setWithFloatingMem((b) => !b)}
        tip="Based on the selected champion lineup"
      />
      <ToggleRow
        label="Class Bonus"
        checked={cardsCtx.withClassBonus}
        onChange={() => cardsCtx.setWithClassBonus((b) => !b)}
        tip="Based on the selected champion lineup"
      />
      <ToggleRow
        label="Element Bonus"
        checked={cardsCtx.withElementBonus}
        onChange={() => cardsCtx.setWithElementBonus((b) => !b)}
      />
      <ToggleRow
        label="Level Bonus"
        checked={cardsCtx.withLevelBonus}
        onChange={() => cardsCtx.setWithLevelBonus((b) => !b)}
      />
      <ToggleRow
        label="Memory Bonus"
        checked={cardsCtx.withMemoryBonus}
        onChange={() => cardsCtx.setWithMemoryBonus((b) => !b)}
      />

      <div className="group-label">Mechanics</div>
      <ToggleRow
        label="Only multi-class cards"
        checked={cardsCtx.onlyMultiClass}
        onChange={() => cardsCtx.setOnlyMultiClass((b) => !b)}
      />
      <ToggleRow
        label="Preparation counters (add)"
        checked={cardsCtx.withAddPrep}
        onChange={() => cardsCtx.setWithAddPrep((b) => !b)}
        tip="Cards that add Preparation counters"
      />
      <ToggleRow
        label="Preparation counters (use)"
        checked={cardsCtx.withUsePrep}
        onChange={() => cardsCtx.setWithUsePrep((b) => !b)}
        tip="Cards that benefit from having Preparation counters"
      />
      <ToggleRow
        label="Grave disruption"
        checked={cardsCtx.withGraveBanish}
        onChange={() => cardsCtx.setWithGraveBanish((b) => !b)}
        tip="Effects that banish cards from graveyards"
      />
      <ToggleRow
        label="Mill (opponent)"
        checked={cardsCtx.withOppMill}
        onChange={() => cardsCtx.setWithOppMill((b) => !b)}
        tip="Effects that move cards from the top of the deck into the graveyard"
      />
      <ToggleRow
        label="Mill (self)"
        checked={cardsCtx.withSelfMill}
        onChange={() => cardsCtx.setWithSelfMill((b) => !b)}
        tip="Effects that move cards from the top of the deck into the graveyard"
      />
    </Section>
  )

  const attributesSection = (
    <Section title="Card attributes" collapsible={false}>
      <TextField
        style={{ marginTop: 6 }}
        label="search"
        id="deck-cards-search"
        helperText={
          <>
            Search card name or effect text. <br /> Note: ignores any text
            within parenthesis
          </>
        }
        onChange={(e) => cardsCtx.setSearch(e.target.value)}
        value={cardsCtx.search}
        size="small"
      />
      <FormControl size="small">
        <InputLabel id="legality-label">legality</InputLabel>
        <SingleSelect
          label="legality"
          labelId="legality-label"
          value={cardsCtx.legality}
          onChange={(e) => cardsCtx.setLegality(e.target.value)}
        >
          <MenuItem value={"any"}>any</MenuItem>
          <MenuItem value={"standard"}>standard legal</MenuItem>
          <MenuItem value={"pantheon"}>pantheon only</MenuItem>
          <MenuItem value={"draft"}>draft only</MenuItem>
        </SingleSelect>
      </FormControl>
      <Select
        label="element"
        id="select-card-element"
        options={cardsCtx.elementOpts ?? []}
        value={cardsCtx.elements ?? []}
        onChange={cardsCtx.setElements}
      />
      <Select
        label="types"
        id="select-card-types"
        options={cardsCtx.typeOpts ?? []}
        value={cardsCtx.types ?? []}
        onChange={cardsCtx.setTypes}
      />
      <Select
        label="subtypes"
        id="select-card-subtypes"
        options={cardsCtx.subtypeOpts ?? []}
        value={cardsCtx.subtypes ?? []}
        onChange={cardsCtx.setSubtypes}
      />
      <Select
        label="classes"
        id="select-card-classes"
        options={cardsCtx.classOpts ?? []}
        value={cardsCtx.classes ?? []}
        onChange={cardsCtx.setClasses}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Select
          label="keywords"
          id="select-card-keywords"
          options={cardsCtx.keywordOpts ?? []}
          value={cardsCtx.keywords ?? []}
          onChange={cardsCtx.setKeywords}
        />
        <Tip
          msg={`Note: "keywords" filter intentionally ignores text within parenthesis. Text in parenthesis is always rules text, which can accidentally match a card for "keywords" that it does not actually possess.`}
        />
      </div>
      <FormControl size="small">
        <InputLabel id="speed-label">speed</InputLabel>
        <SingleSelect
          label="speed"
          labelId="speed-label"
          value={cardsCtx.speed}
          onChange={(e) => {
            const value = e.target.value
            if (value === "any") return cardsCtx.setSpeed("any")
            return cardsCtx.setSpeed(value)
          }}
        >
          <MenuItem value={"any"}>any</MenuItem>
          <MenuItem value={false}>slow</MenuItem>
          <MenuItem value={true}>fast</MenuItem>
        </SingleSelect>
      </FormControl>
      <StatFilter
        label="play cost"
        value={cardsCtx.cost}
        setValue={cardsCtx.setCost}
        op={cardsCtx.operator}
        setOp={cardsCtx.setOperator}
      />
      <StatFilter
        label="power"
        value={cardsCtx.power}
        setValue={cardsCtx.setPower}
        op={cardsCtx.powOp}
        setOp={cardsCtx.setPowOp}
      />
      <StatFilter
        label="life"
        value={cardsCtx.life}
        setValue={cardsCtx.setLife}
        op={cardsCtx.lifeOp}
        setOp={cardsCtx.setLifeOp}
      />
    </Section>
  )

  const metagameSection = (
    <Section
      title="Metagame"
      collapsible={false}
      badge={
        META_TIERS.find((t) => t.value === cardsCtx.metaTier)?.badge ?? null
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <FormControl size="small" style={{ flex: 1 }}>
          <InputLabel id="meta-tier-label">win rate (60 days)</InputLabel>
          <SingleSelect
            label="win rate (60 days)"
            labelId="meta-tier-label"
            value={cardsCtx.metaTier}
            onChange={(e) => cardsCtx.setMetaTier(e.target.value)}
            renderValue={(v) => {
              const t = META_TIERS.find((tier) => tier.value === v)
              return t?.detail ? `${t.name} ${t.detail}` : t?.name
            }}
          >
            {META_TIERS.map((t) => (
              <MenuItem key={t.value} value={t.value} style={{ fontSize: 13 }}>
                {t.name}
                {t.detail && (
                  <span
                    style={{
                      marginLeft: "auto",
                      paddingLeft: 16,
                      color: "var(--muted)",
                    }}
                  >
                    {t.detail}
                  </span>
                )}
              </MenuItem>
            ))}
          </SingleSelect>
        </FormControl>
        <Tip
          msg={metaTierTip}
        />
      </div>
    </Section>
  )

  const sortSection = (
    <Section title="Sort & sets" collapsible={false}>
      <div className="sort-input" style={{ display: "flex", gap: 4 }}>
        <FormControl size="small" style={{ flex: 3 }}>
          <InputLabel id="sort-by-input-label">sort by</InputLabel>
          <SingleSelect
            value={cardsCtx.sort}
            onChange={(e) => cardsCtx.setSort(e.target.value)}
            labelId="sort-by-input-label"
            label="sort by"
          >
            {(cardsCtx.sortOpts ?? []).map((opt) => {
              return (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              )
            })}
          </SingleSelect>
        </FormControl>
        <FormControl size="small" style={{ flex: 2 }}>
          <SingleSelect
            value={cardsCtx.sortDir}
            onChange={(e) => cardsCtx.setSortDir(e.target.value)}
          >
            {(cardsCtx.sortDirOpts ?? []).map((opt) => {
              return (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              )
            })}
          </SingleSelect>
        </FormControl>
      </div>
      <Select
        label="set names"
        id="select-card-set-names"
        options={cardsCtx.setNameOpts ?? []}
        value={cardsCtx.setNames ?? []}
        onChange={cardsCtx.setSetNames}
      />
      <Select
        label="rarities"
        id="select-card-rarities"
        options={cardsCtx.rarityOpts ?? []}
        value={cardsCtx.rarities ?? []}
        onChange={cardsCtx.setRarities}
      />
    </Section>
  )

  const deckFilters = (
    <>
      <div
        style={{
          position: "sticky",
          top: -12,
          zIndex: 3,
          background: "var(--surface)",
          margin: "-12px -12px 2px",
          padding: "10px 14px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: 12.5, color: "var(--muted)" }}>
          Matching results
        </span>
        {resultCount}
      </div>
      {collectionSection}
      {lineupSection}
      {focusCard && (
        <div
          ref={previewRef}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            padding: "10px 0px",
          }}
        >
          <Card
            card={focusCard}
            imgUrl={focusCard?.computed?.imgUrl}
            height={previewHeight}
            shadow
          />
        </div>
      )}
      {/* keep the sections mounted during the hover preview so the
          pile's mouseleave still fires and section state survives */}
      <div style={{ display: focusCard ? "none" : "contents" }}>
        {synergiesSection}
        {attributesSection}
        {metagameSection}
        {sortSection}
        <ExclusionFilters />
      </div>
    </>
  )

  const handleExplore = () => {
    champCtx.setFocus(trackedList)
    // on phones the drawer is a full overlay — close it so the
    // card grid is actually visible
    if (appSizeCtx.isSmall) {
      drawerCtx.setOpen(false)
    }
  }

  const welcome = (
    <div className="welcome">
      <h2>Find exactly the cards you need</h2>
      <p className="welcome-intro">
        Pick the champions you plan to play and they become the base filter
        for everything else. When your lineup is ready, explore synergies to
        search every card, and track your collection as you go.
      </p>
      <div className="welcome-rule" aria-hidden="true" />
      <button className="btn-primary" onClick={handleExplore}>
        Explore synergies
      </button>
    </div>
  )

  const filters =
    modeCtx.mode === "prints" ? (
      <div>print filters</div>
    ) : champCtx.focus ? (
      deckFilters
    ) : (
      <>
        {welcome}
        <Acknowledgements />
      </>
    )

  return (
    <div ref={appSizeCtx.elementRef} className="app fh">
      <div
        className="drawer fh"
        style={{ ...(!drawerCtx.open && { width: 0 }) }}
      >
        <div className="header">
          <button
            type="button"
            className="drawer-action"
            onClick={handleSettingsClick}
          >
            <SettingsOutlinedIcon />
            Settings
          </button>
          <button
            type="button"
            className="drawer-action"
            onClick={() => {
              // still picking champion
              // so only clear those filters:
              if (!champCtx.focus) {
                return champCtx.handleClear()
              }
              // otherwise champion was picked,
              // so only clear filters on synergies view:
              cardsCtx.handleClear()
              if (appSizeCtx.isSmall) {
                drawerCtx.setOpen(false)
              }
              if (controlsRef.current) {
                controlsRef.current.scrollTop = 0
              }
            }}
            disabled={!champCtx.focus}
          >
            <ReplayIcon />
            Reset filters
          </button>
          <button
            type="button"
            className="drawer-action danger"
            onClick={() => {
              const confirmed = window.confirm("Return to champion selection?")
              if (!confirmed) {
                return
              }
              champCtx.handleClear()
              cardsCtx.handleClear()
              if (appSizeCtx.isSmall) {
                drawerCtx.setOpen(false)
              }
            }}
            disabled={champCtx.clearDisabled}
          >
            <RestartAltIcon />
            Start over
          </button>
          <Menu
            anchorEl={settingsAnchorEl}
            open={settingsOpen}
            onClose={handleSettingsClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            MenuListProps={{ dense: true, sx: { py: 0.5 } }}
          >
            <div style={menuSectionLabelStyle}>Card details</div>
            <MenuItem
              dense
              disableRipple
              onClick={(e) => e.stopPropagation()}
              style={menuItemStyle}
            >
              <FormControlLabel
                style={{ margin: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={!!settingsState?.showPrice}
                    onChange={togglePrice}
                  />
                }
                label="Show price"
              />
            </MenuItem>
            {isLoggedIn && (
              <MenuItem
                dense
                disableRipple
                onClick={(e) => e.stopPropagation()}
                style={menuItemStyle}
              >
                <FormControlLabel
                  style={{ margin: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={!!settingsState?.showOwned}
                      onChange={toggleOwned}
                    />
                  }
                  label="Show owned"
                />
              </MenuItem>
            )}
            <MenuItem
              dense
              disableRipple
              onClick={(e) => e.stopPropagation()}
              style={menuItemStyle}
            >
              <FormControlLabel
                style={{ margin: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={!!settingsState?.showPrint}
                    onChange={togglePrint}
                  />
                }
                label="Show print run"
              />
            </MenuItem>
            <MenuItem
              dense
              disableRipple
              onClick={(e) => e.stopPropagation()}
              style={menuItemStyle}
            >
              <FormControlLabel
                style={{ margin: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={!!settingsState?.showWinRate}
                    onChange={toggleWinRate}
                  />
                }
                label="Show win rate (60d)"
              />
            </MenuItem>
            <Divider />
            <div style={{ ...menuSectionLabelStyle, marginTop: 2 }}>
              Always hidden
            </div>
            <MenuItem
              dense
              disableRipple
              onClick={(e) => e.stopPropagation()}
              style={menuItemStyle}
            >
              <FormControlLabel
                style={{ margin: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={!!ignoreEnabled}
                    onChange={toggleIgnore}
                  />
                }
                label="Enable hiding"
              />
            </MenuItem>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxHeight: 180,
                overflowY: "auto",
                padding: "6px 10px",
                margin: "6px 10px",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 6,
              }}
            >
              {ignoredList && ignoredList.length ? (
                <List dense disablePadding>
                  {ignoredList.map((n) => (
                    <ListItem key={n} disableGutters sx={{ py: 0 }}>
                      <ListItemText
                        primary={n}
                        primaryTypographyProps={{ fontSize: 12 }}
                      />
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`remove ${n}`}
                        title="Remove from hidden"
                        onClick={(e) => {
                          e.stopPropagation()
                          removeFromIgnored(n)
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--muted)",
                    padding: "4px 0",
                  }}
                >
                  No hidden cards
                </div>
              )}
            </div>
          </Menu>
          {appSizeCtx.isSmall && (
            <DrawerButton
              className="drawer-toggle close"
              onClick={drawerCtx.toggle}
              icon="close"
            />
          )}
        </div>
        <div className="controls" ref={controlsRef}>
          {filters}
        </div>
      </div>
      <div className="main fh">
        {drawerCtx.open && (
          <div
            className="drawer-backdrop"
            onClick={() => drawerCtx.setOpen(false)}
          />
        )}
        <div className="header">
          <DrawerButton
            className="drawer-toggle open"
            onClick={drawerCtx.toggle}
            icon="filter"
          />
          <div className="app-title">Grand Archive Helper</div>
          {/* on phones this would collide with the corner buttons;
              the acks footer shows the date there instead */}
          {!appSizeCtx.isSmall && (
            <Tip
              style={{ marginLeft: 0 }}
              placement="bottom"
              icon={InfoOutlinedIcon}
              label="Last updated"
              msg={`Last updated: ${release}`}
            />
          )}
          <div
            style={{
              position: "absolute",
              right: 12,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <IconButton
              size="small"
              onClick={toggleTheme}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              sx={{ color: "var(--muted)" }}
            >
              {isDark ? (
                <LightModeOutlinedIcon fontSize="small" />
              ) : (
                <DarkModeOutlinedIcon fontSize="small" />
              )}
            </IconButton>
            <UserMenu />
          </div>
        </div>
        {champCtx.focus ? (
          <CardGrid cards={cardsCtx.cards} />
        ) : (
          <ManualChampPicker
            onSelect={setTrackedList}
            onExplore={handleExplore}
          />
        )}
      </div>
      <LoginModal />
      <MigrationDialog />
      <Dialog
        open={csvWarnOpen}
        onClose={() => setCsvWarnOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--surface-2)",
            border: "1px solid var(--border)",
            backgroundImage: "none",
          },
        }}
      >
        <div style={{ padding: "20px 24px 24px" }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--text)",
              marginBottom: 10,
            }}
          >
            Upload to your account?
          </div>
          <p style={{ fontSize: 13, color: "var(--text)", margin: "0 0 8px" }}>
            This CSV becomes your account's collection, replacing whatever it
            currently holds.
          </p>
          <p
            style={{ fontSize: 12, color: "var(--muted)", margin: "0 0 16px" }}
          >
            From then on your account is the single source of truth:
            collections stored locally on other devices will be cleared when
            you log in there, to prevent accidental overwrites.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="outlined"
              style={{ flex: 1 }}
              onClick={() => setCsvWarnOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              style={{ flex: 1 }}
              onClick={() => {
                setCsvWarnOpen(false)
                triggerCsvPicker()
              }}
            >
              Choose file
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

const Themed = ({ children }) => {
  const { state } = useSettings()
  const mode = state.themeMode === "dark" ? "dark" : "light"
  const theme = useMemo(() => getTheme(mode), [mode])
  // layout effect so the CSS variables land before paint (no light flash)
  useLayoutEffect(() => {
    applyCssPalette(mode)
  }, [mode])
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}

export default function Main() {
  return (
    <FeedbackFish projectId="859e20cb9e0f2a">
      <AuthProvider>
        <SettingsProvider>
        <Themed>
          <IgnoredContextProvider>
            <PrepCounterProvider>
              <MillEffectsProvider>
                <FloatingMemProvider>
                  <GraveBanishProvider>
                    <ModeContextProvider>
                      <DeckContextProvider>
                        <ChampionContextProvider>
                          <CardsContextProvider>
                            <DrawerContextProvider>
                              <AppSizeContextProvider>
                                <App />
                              </AppSizeContextProvider>
                            </DrawerContextProvider>
                          </CardsContextProvider>
                        </ChampionContextProvider>
                      </DeckContextProvider>
                    </ModeContextProvider>
                  </GraveBanishProvider>
                </FloatingMemProvider>
              </MillEffectsProvider>
            </PrepCounterProvider>
          </IgnoredContextProvider>
        </Themed>
        </SettingsProvider>
      </AuthProvider>
    </FeedbackFish>
  )
}
